import React, { useState } from 'react';
import useAuthStore from '../store/useAuthStore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useNotificationStore from '../store/useNotificationStore';
import { authFetch, dataFetch } from '../api';

const { FiMail, FiLock, FiUser, FiCheck, FiArrowRight } = FiIcons;

const AuthScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const login = useAuthStore((state) => state.login);
  const { addNotification } = useNotificationStore();

  const confirmSession = async (fallbackUser) => {
    const sessionResponse = await authFetch('get-session');
    const user = sessionResponse?.data?.user || fallbackUser;

    if (sessionResponse?.status !== 'success' || !user?.id) {
      return {
        success: false,
        error: sessionResponse?.error || 'Nie udało się potwierdzić sesji.'
      };
    }

    localStorage.setItem('user_id', user.id);
    return { success: true, user };
  };

  const createProfile = async (user, displayName) => {
    const response = await dataFetch(
      `create/profiles?owner_id=${encodeURIComponent(user.id)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          username: displayName,
          first_name: '',
          last_name: '',
          nip: '',
          company: '',
          phone: '',
          address: '',
          updated_at: Date.now()
        })
      }
    );

    return response?.status === 'success';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isLogin) {
      if (!email || !password) {
        setError('Proszę podać adres e-mail i hasło.');
        return;
      }

      setIsSubmitting(true);
      const response = await authFetch('sign-in/email', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password
        })
      });

      if (response?.status !== 'success') {
        setError(response?.error || 'Nieprawidłowy login lub hasło. Spróbuj ponownie.');
        setIsSubmitting(false);
        return;
      }

      const session = await confirmSession(response?.data?.user);
      if (!session?.success) {
        setError(session?.error);
        setIsSubmitting(false);
        return;
      }

      login(session.user);
      addNotification('success', 'Zalogowano pomyślnie.');
      window.location.reload();
      return;
    }

    if (!email || !password || !confirmPassword || !username) {
      setError('Proszę wypełnić wszystkie pola.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne.');
      return;
    }
    if (!acceptedTerms) {
      setError('Proszę zaakceptować warunki korzystania z usługi.');
      return;
    }

    setIsSubmitting(true);
    const response = await authFetch('sign-up/email', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        password,
        name: username.trim()
      })
    });

    if (response?.status !== 'success') {
      setError(response?.error || 'Nie udało się utworzyć konta. Spróbuj ponownie.');
      setIsSubmitting(false);
      return;
    }

    const session = await confirmSession(response?.data?.user);
    if (!session?.success) {
      setError(session?.error);
      setIsSubmitting(false);
      return;
    }

    const profileCreated = await createProfile(session.user, username.trim());
    if (!profileCreated) {
      setError('Konto zostało utworzone, ale nie udało się zapisać profilu. Spróbuj zalogować się ponownie.');
      setIsSubmitting(false);
      return;
    }

    login(session.user);
    addNotification('success', 'Konto i profil zostały utworzone.');
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden m-4">
        <div className="p-8 md:p-12">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isLogin ? 'Witaj ponownie' : 'Utwórz konto'}
            </h1>
            <p className="text-gray-500">
              {isLogin 
                ? 'Zaloguj się, aby kontynuować pracę nad projektami' 
                : 'Dołącz do nas i zacznij projektować swoje wnętrza'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiUser} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Nazwa użytkownika"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none transition-all placeholder-gray-400"
                />
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiMail} className="text-gray-400" />
              </div>
              <input
                type="email"
                placeholder="Adres e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none transition-all placeholder-gray-400"
              />
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input
                type="password"
                placeholder="Hasło"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none transition-all placeholder-gray-400"
              />
            </div>

            {!isLogin && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiCheck} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  placeholder="Potwierdź hasło"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none transition-all placeholder-gray-400"
                />
              </div>
            )}

            {!isLogin && (
              <div className="flex items-center">
                <input
                  id="terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="h-4 w-4 text-[#1E2937] focus:ring-[#1E2937] border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-600 cursor-pointer select-none">
                  Akceptuję <a href="#" className="text-[#1E2937] font-semibold hover:underline">Warunki korzystania z usługi</a>
                </label>
              </div>
            )}

            {isLogin && (
              <div className="flex justify-end">
                <button 
                  type="button" 
                  className="text-sm font-medium text-[#1E2937] hover:underline"
                  onClick={() => addNotification('info', 'Funkcja resetowania hasła zostanie wysłana na e-mail.')}
                >
                  Nie pamiętasz hasła?
                </button>
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-100 text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-[#1E2937] hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E2937] transition-all transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
            >
              {isSubmitting ? 'Proszę czekać...' : isLogin ? 'Zaloguj się' : 'Zarejestruj się'}
              {!isSubmitting && <SafeIcon icon={FiArrowRight} />}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {isLogin ? 'Nie masz jeszcze konta?' : 'Masz już konto?'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="ml-2 font-bold text-[#1E2937] hover:underline focus:outline-none"
              >
                {isLogin ? 'Zarejestruj się' : 'Zaloguj się'}
              </button>
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 text-center border-t border-gray-100">
           <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} FloorCraft Pro. Wszystkie prawa zastrzeżone.
           </p>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;